import type { ActivityInput } from '@orus.eu/activity'
import {
  AmountDimension,
  BaseDimension,
  BooleanDimension,
  ExclusionDimension,
  FinancialRateDimension,
  FinancialRateDimensionWithRetrocompatibleInstallmentFeeRateDimension,
  FinancialRateWithUnsetDimension,
  IntegerDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import { rcpaProductVersions } from '@orus.eu/product'

export * from './rcpa-activities-model/index.ts'
export * from './rcpa-agent-dimension.ts'
export * from './rcpa-department-dimension.ts'
export * from './rcpa-guarantee-text.ts'
export * from './rcpa-offer-dimension.ts'
export * from './rcpa-options-dimension.ts'
export * from './rcpa-quote/index.ts'
export * from './rcpa-subsidiaries-dimension.ts'

export const rcpaProductVersionDimension = new StringsEnumDimension({
  name: 'rcpaProductVersion',
  displayKeys: { name: 'dimension_name_rcpa_product_version' },
  values: rcpaProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaAvailableDimension = new BooleanDimension({
  name: 'rcpaAvailable',
  displayKeys: { name: 'dimension_name_rcpa_available' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaHasOptionDirigeantAvailableDimension = new BooleanDimension({
  name: 'rcpaHasOptionDirigeantAvailable',
  displayKeys: { name: 'dimension_name_rcpa_has_option_dirigeant_available' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaOptionDirigeantDimension = new BooleanDimension({
  name: 'rcpaOptionDirigeant',
  displayKeys: { name: 'dimension_name_rcpa_option_dirigeant' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaHasAgentsDimension = new BooleanDimension({
  name: 'rcpaHasAgents',
  displayKeys: { name: 'dimension_name_rcpa_has_agents' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isRcpaCifWorkforceNeededDimension = new BooleanDimension({
  name: 'isRcpaCifWorkforceNeeded',
  displayKeys: { name: 'dimension_name_is_rcpa_cif_workforce_needed' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaCifWorkforceDimension = new IntegerDimension({
  name: 'rcpaCifWorkforce',
  displayKeys: { name: 'dimension_name_rcpa_cif_workforce' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isRcpaPersonaNeededDimension = new BooleanDimension({
  name: 'isRcpaPersonaNeeded',
  displayKeys: { name: 'dimension_name_is_rcpa_persona_needed' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaPersona = ['physique', 'morale'] as const

export type RcpaPersona = (typeof rcpaPersona)[number]

export function isRcpaPersona(persona: string): persona is RcpaPersona {
  return rcpaPersona.includes(persona as RcpaPersona)
}

export const rcpaPersonaDimension = new StringsWithDataEnumDimension({
  name: 'rcpaPersona',
  displayKeys: { name: 'dimension_name_rcpa_persona' },
  entries: rcpaPersona.map((persona) => [
    persona,
    {
      label:
        persona === 'physique'
          ? '🧑‍💼 Je travaille en mon nom (micro-entreprise, EI même si je suis mandataire d’un réseau immobilier)'
          : "🏢 J'ai créé une société (EURL, SASU, SARL, etc.)",
    },
  ]),
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaExclusionsDimension = new ExclusionDimension({
  name: 'rcpaExclusions',
  displayKeys: { name: 'dimension_name_rcpa_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
})

export const rcpaYearlyBasePremiumDimension = new AmountDimension({
  name: 'rcpaYearlyBasePremium',
  displayKeys: { name: 'dimension_name_rcpa_yearly_base_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaYearlyTaxesDimension = new AmountDimension({
  name: 'rcpaYearlyTaxes',
  displayKeys: { name: 'dimension_name_rcpa_yearly_taxes' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaYearlyTotalPremiumDimension = new AmountDimension({
  name: 'rcpaYearlyTotalPremium',
  displayKeys: { name: 'dimension_name_rcpa_yearly_total_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaMonthlyTotalPremiumDimension = new AmountDimension({
  name: 'rcpaMonthlyTotalPremium',
  displayKeys: { name: 'dimension_name_rcpa_monthly_total_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaYearlyOrusManagementFeeAmountDimension = new AmountDimension({
  name: 'rcpaYearlyOrusManagementFeeAmount',
  displayKeys: { name: 'dimension_name_rcpa_yearly_orus_management_fee_amount' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaYearlyOrusInstallmentFeeAmountDimension = new AmountDimension({
  name: 'rcpaYearlyOrusInstallmentFeeAmount',
  displayKeys: { name: 'dimension_name_rcpa_yearly_orus_installment_fee_amount' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaYearlyPartnerManagementFeeAmountDimension = new AmountDimension({
  name: 'rcpaYearlyPartnerManagementFeeAmount',
  displayKeys: { name: 'dimension_name_rcpa_yearly_partner_management_fee_amount' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isRcpaOriasRegulatedActivityDimension = new BooleanDimension({
  name: 'isRcpaOriasRegulatedActivity',
  displayKeys: { name: 'dimension_name_is_rcpa_orias_regulated_activity' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isRcpaAlreadyAigInsuredWithDerogationDimension = new BooleanDimension({
  name: 'isRcpaAlreadyAigInsuredWithDerogation',
  displayKeys: { name: 'dimension_name_is_rcpa_already_aig_insured_with_derogation' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaActivitiesDimension = new BaseDimension<'rcpaActivities', ActivityInput[]>({
  name: 'rcpaActivities',
  displayKeys: { name: 'dimension_name_rcpa_activities' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const defaultRcpaManagementFeeRateDimension = new FinancialRateDimension({
  name: 'defaultRcpaManagementFeeRate',
  displayKeys: { name: 'dimension_name_default_rcpa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetRcpaManagementFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetRcpaManagementFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_rcpa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaManagementFeeRateDimension = new FinancialRateDimension({
  name: 'rcpaManagementFeeRate',
  displayKeys: { name: 'dimension_name_rcpa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const maxRcpaManagementFeeRateDimension = new FinancialRateDimension({
  name: 'maxRcpaManagementFeeRate',
  displayKeys: { name: 'dimension_name_max_rcpa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const defaultRcpaInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'defaultRcpaInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_default_rcpa_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetRcpaInstallmentFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetRcpaInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_rcpa_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const maxRcpaInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'maxRcpaInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_max_rcpa_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaInstallmentFeeRateDimension = new FinancialRateDimensionWithRetrocompatibleInstallmentFeeRateDimension(
  {
    name: 'rcpaInstallmentFeeRate',
    displayKeys: { name: 'dimension_name_rcpa_installment_fee_rate' },
    considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
    includeInTrackingMessages: false,
    inputLocking: undefined,
  } as const,
)

export const rcpaMultiActivityAvailableDimension = new BooleanDimension({
  name: 'rcpaMultiActivityAvailable',
  displayKeys: { name: 'dimension_name_rcpa_multi_activity_available' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaAvailableActivitiesForMultiActivityDimension = new BaseDimension<
  'rcpaAvailableActivitiesForMultiActivity',
  ActivityInput[]
>({
  name: 'rcpaAvailableActivitiesForMultiActivity',
  displayKeys: { name: 'dimension_name_rcpa_available_activities_for_multi_activity' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaAvailableAgentsActivitiesDimension = new BaseDimension<
  'rcpaAvailableAgentsActivities',
  ActivityInput[]
>({
  name: 'rcpaAvailableAgentsActivities',
  displayKeys: { name: 'dimension_name_rcpa_available_agents_activities' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
